import { defineStore } from 'pinia';
import { pcCardCreatePre, pcCardCreateV2, pcCardTemplateList } from '@/api/card';
import { getDeviceId } from '@/utils/device';

function normalizeText(value) {
  if (value === null || value === undefined) {
    return '';
  }
  const text = String(value).trim();
  if (!text || text.toLowerCase() === 'null' || text.toLowerCase() === 'undefined') {
    return '';
  }
  return text;
}

function createSceneKey(scene = {}) {
  return normalizeText(scene?.sceneId)
    || normalizeText(scene?.id)
    || normalizeText(scene?.scene)
    || normalizeText(scene?.sceneName)
    || normalizeText(scene?.name);
}

function createTemplateKey(template = {}) {
  return [
    normalizeText(template?.templateId),
    normalizeText(template?.channelType),
    normalizeText(template?.network),
    normalizeText(template?.openAmount),
    normalizeText(template?.openChargeAmount),
    normalizeText(template?.feeCharge),
    normalizeText(template?.feePay),
    normalizeText(template?.feeWithdraw),
    normalizeText(template?.consumptionLimit),
    normalizeText(template?.consumptionLimitSymbol)
  ].join('__');
}

function normalizeSceneList(sceneList = []) {
  if (!Array.isArray(sceneList)) {
    return [];
  }

  const map = new Map();
  sceneList.forEach((scene) => {
    if (!scene || typeof scene !== 'object') {
      return;
    }
    const key = createSceneKey(scene);
    if (!key) {
      return;
    }
    if (!map.has(key)) {
      map.set(key, { ...scene });
    }
  });
  return Array.from(map.values());
}

function mergeTemplateList(list = []) {
  if (!Array.isArray(list)) {
    return [];
  }

  const templateMap = new Map();

  list.forEach((item) => {
    if (!item || typeof item !== 'object') {
      return;
    }

    const templateKey = createTemplateKey(item);
    const normalizedScenes = normalizeSceneList(item.sceneList);

    if (!templateMap.has(templateKey)) {
      templateMap.set(templateKey, {
        ...item,
        sceneList: normalizedScenes
      });
      return;
    }

    const current = templateMap.get(templateKey);
    current.sceneList = normalizeSceneList([...(current.sceneList || []), ...normalizedScenes]);
  });

  return Array.from(templateMap.values());
}

function defaultState() {
  return {
    loading: false,
    submitting: false,
    cardType: 2,
    holderStatus: 0,
    rejectReason: '',
    holderInfo: {},
    templates: [],
    currentIndex: 0,
    currentSceneIndex: 0
  };
}

export const useCardOpenStore = defineStore('card-open', {
  state: () => defaultState(),
  getters: {
    currentTemplate(state) {
      return state.templates[state.currentIndex] || {};
    },
    currentScenes() {
      return Array.isArray(this.currentTemplate.sceneList) ? this.currentTemplate.sceneList : [];
    },
    currentScene() {
      return this.currentScenes[this.currentSceneIndex] || {};
    },
    totalOpenAmount() {
      const openAmount = Number(this.currentTemplate.openAmount || 0);
      const openChargeAmount = Number(this.currentTemplate.openChargeAmount || 0);
      return openAmount + openChargeAmount;
    }
  },
  actions: {
    reset() {
      Object.assign(this, defaultState());
    },
    selectTemplate(index) {
      this.currentIndex = index >= 0 ? index : 0;
      this.currentSceneIndex = 0;
    },
    selectScene(index) {
      this.currentSceneIndex = index >= 0 ? index : 0;
    },
    async load({ address, cardType = 2 } = {}) {
      this.loading = true;
      this.cardType = Number(cardType || 2);
      try {
        const tasks = [
          pcCardTemplateList({
            address: address || null,
            cardType: this.cardType
          }),
          pcCardCreatePre({ address: address || null })
        ];

        const [templateResult, preResult] = await Promise.all(tasks);
        this.templates = mergeTemplateList(templateResult?.list);
        this.holderStatus = Number(preResult?.holderStatus || 0);
        this.rejectReason = preResult?.reason || '';
        this.holderInfo = {
          firstName: preResult?.firstName || '',
          lastName: preResult?.lastName || ''
        };
        if (this.currentIndex >= this.templates.length) {
          this.currentIndex = 0;
        }
        this.currentSceneIndex = 0;
        return this.templates;
      } finally {
        this.loading = false;
      }
    },
    async submit({ address, pcbId } = {}) {
      this.submitting = true;
      try {
        return await pcCardCreateV2({
          address: address || null,
          templateId: String(this.currentTemplate.templateId || ''),
          invitationCode: '',
          scene: String(this.currentScene.sceneId || this.currentScene.scene || ''),
          deviceToken: getDeviceId(),
          pcbId
        });
      } finally {
        this.submitting = false;
      }
    }
  }
});
